import type { FulfillmentOrderDetail, ScanCodeResponse } from '@/infrastructure/FulfillmentAdminClient';
import { readScanCode } from '@/pages/fulfillment/components/scanReading';
import { parcelNumbersOf } from './arrivalSortRules';

/** The two reads the desk makes; both are lookups, so a scan here changes nothing on the server (rule 14). */
export interface ArrivalSortReader {
  scanCode(request: { code: string }): Promise<ScanCodeResponse>;
  getOrderDetail(orderId: number): Promise<FulfillmentOrderDetail>;
}

/** What the scanned code turned out to be when it is not an order's inbound parcel (rule 1). */
export type NotAParcel =
  | { kind: 'INBOUND_REQUEST'; docId: string | null; inboundNo: string | null }
  | { kind: 'RETURN_WAYBILL'; orderId: number | null }
  | { kind: 'PRODUCT_BARCODE'; sku: string | null; productName: string | null }
  | { kind: 'UNKNOWN' };

export type ArrivalLookup =
  | { kind: 'ORDER'; code: string; details: FulfillmentOrderDetail[]; truncated: boolean }
  | ({ code: string } & NotAParcel);

/** A bundle beyond this many orders is shown as far as it was read, and says so. */
export const MAX_BUNDLE_ORDERS = 8;
/** Sibling parcels looked up to find orders sharing them; each is one read. */
export const MAX_SIBLING_LOOKUPS = 12;

const orderIdsOf = (response: ScanCodeResponse): number[] => {
  const ids: number[] = [];
  if (response.kind === 'ORDER_WAYBILL' && response.orderId != null) {
    ids.push(response.orderId);
  }
  for (const match of response.alsoMatched ?? []) {
    if (match.kind === 'ORDER' && match.orderId != null && !ids.includes(match.orderId)) {
      ids.push(match.orderId);
    }
  }
  return ids;
};

/**
 * Reads the parcel in hand and the whole bundle it belongs to (rule 2): the orders the code names, then,
 * hop by hop, the orders sharing any other parcel of an order already read. A bundle bigger than the caps,
 * or one whose sibling lookup failed, comes back truncated, and the verdict then refuses to sort it.
 */
export const lookUpArrival = async (reader: ArrivalSortReader, code: string): Promise<ArrivalLookup> => {
  const first = readScanCode(await reader.scanCode({ code }));
  const firstIds = orderIdsOf(first);
  if (firstIds.length === 0) {
    switch (first.kind) {
      case 'INBOUND_REQUEST':
        return { kind: 'INBOUND_REQUEST', code, docId: first.docId ?? null, inboundNo: first.inboundNo ?? null };
      case 'RETURN_WAYBILL':
        return { kind: 'RETURN_WAYBILL', code, orderId: first.orderId ?? null };
      case 'PRODUCT_BARCODE':
        return { kind: 'PRODUCT_BARCODE', code, sku: first.sku ?? null, productName: first.productName ?? null };
      default:
        return { kind: 'UNKNOWN', code };
    }
  }
  if (first.kind === 'RETURN_WAYBILL') {
    // The customer's own waybill is a parcel coming back, not one coming in to be sorted.
    return { kind: 'RETURN_WAYBILL', code, orderId: first.orderId ?? null };
  }

  const details = new Map<number, FulfillmentOrderDetail>();
  const looked = new Set<string>([code.trim().toUpperCase()]);
  let lookups = 0;
  let truncated = false;
  let frontier = firstIds;
  // Follow shared parcels until no new order turns up (rule 2): A–B share Y, B–C share Z, so X reaches C.
  while (frontier.length > 0) {
    const wanted = frontier.filter((id) => !details.has(id));
    const room = MAX_BUNDLE_ORDERS - details.size;
    if (wanted.length > room) {
      truncated = true;
    }
    const firstHop = details.size === 0;
    const read = await Promise.all(
      wanted.slice(0, Math.max(room, 0)).map((id) =>
        firstHop
          ? reader.getOrderDetail(id)
          : reader.getOrderDetail(id).catch(() => {
              // An order further out that could not be read leaves the bundle partial, which the verdict refuses.
              truncated = true;
              return null;
            }),
      ),
    );
    const fresh: FulfillmentOrderDetail[] = [];
    for (const detail of read) {
      if (detail) {
        details.set(detail.header.orderId, detail);
        fresh.push(detail);
      }
    }
    const siblings: string[] = [];
    for (const detail of fresh) {
      for (const line of detail.lines) {
        for (const number of parcelNumbersOf(detail, line)) {
          if (!looked.has(number.toUpperCase())) {
            looked.add(number.toUpperCase());
            siblings.push(number);
          }
        }
      }
    }
    const allowed = Math.max(MAX_SIBLING_LOOKUPS - lookups, 0);
    if (siblings.length > allowed) {
      truncated = true;
    }
    const batch = siblings.slice(0, allowed);
    lookups += batch.length;
    const shared = await Promise.all(
      batch.map((number) =>
        reader.scanCode({ code: number }).then(
          (response) => orderIdsOf(readScanCode(response)),
          () => {
            // A sibling that could not be read may hide an order sharing it, so the bundle is partial.
            truncated = true;
            return [] as number[];
          },
        ),
      ),
    );
    frontier = [...new Set(shared.flat())].filter((id) => !details.has(id));
    if (truncated) {
      break;
    }
  }

  return { kind: 'ORDER', code, details: [...details.values()], truncated };
};
